import {Observable} from 'rxjs';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDeleteDialogObservable, showAlertObservable, showDialogObservable} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';

import * as Api from './api';
import * as Actions from './actions';
import Product from './model';
import * as Constants from './constants';

export const fetchObservable = (productId, includePricesForAllPricingGroups) => {
    return Api.fetchProduct(productId, includePricesForAllPricingGroups).map(newProductsData =>
        Actions.fetchSuccess.action(newProductsData.products),
    );
};

export const fetchAllObservable = (
    institutionId,
    validOn,
    validTo,
    ageGroupId,
    includeDiscontinued,
    filterCurrent = true,
    includePricesForAllPricingGroups,
) => {
    return Api.fetchProductsObservable(
        institutionId,
        validOn,
        validTo,
        ageGroupId,
        includeDiscontinued,
        includePricesForAllPricingGroups,
    ).map(newProductsData =>
        Actions.fetchAllSuccess.action(newProductsData.products, newProductsData.behaviors, filterCurrent),
    );
};

export const createProductObservable = product => {
    return Api.createProduct(Product.toAPI(product)).map(productData =>
        Actions.createSuccess.action(productData.product, productData.product.institutionId),
    );
};

export const updateProductObservable = (product, force = false) => {
    return Api.updateProduct(Product.toAPI(product), force).map(productData =>
        Actions.updateSuccess.action(product, productData.product, productData.product.institutionId),
    );
};

export const removeProductObservable = product => {
    return Api.deleteProduct(Product.toAPI(product)).map(productData =>
        Actions.removeSuccess.action(productData.product, productData.product.institutionId),
    );
};

export const restoreProductObservable = product => {
    return Api.restoreProduct(Product.toAPI(product)).map(productData =>
        Actions.restoreSuccess.action(productData.product, productData.product.institutionId),
    );
};

const unscheduleProductObservable = (productId, last) => {
    return Api.unscheduleProduct(productId).map(productData =>
        Actions.unscheduleSuccess.action(productData.unscheduledProductVersionId, last, productData.remainingVersions),
    );
};

const fetchProduct = action$ =>
    action$
        .ofType(Actions.fetch.type)
        .mergeMap(({payload}) =>
            fetchObservable(payload.productId, payload.includePricesForAllPricingGroups).catch(e =>
                Observable.of(Actions.fetchFailed.action(e)),
            ),
        );

const fetchAll = action$ =>
    action$
        .ofType(Actions.fetchAll.type)
        .mergeMap(({payload}) =>
            fetchAllObservable(
                payload.institutionId,
                payload.validOn,
                payload.validTo,
                payload.ageGroupId,
                payload.includeDiscontinued,
                payload.filterCurrent,
                payload.includePricesForAllPricingGroups,
            ).catch(e => Observable.of(Actions.fetchAllFailed.action(e))),
        );

const createProduct = action$ =>
    action$.ofType(Actions.create.type).mergeMap(({payload}) =>
        createProductObservable(payload.product)
            .flatMap(action => [GlobalEventActions.updateSuccess.action(i18next.t('product.productCreated')), action])
            .catch(e => Observable.of(Actions.createFailed.action(e))),
    );

const getProductBookedDescription = product => {
    if (product.validTo) {
        return i18next.t('product.productBookedDescriptionValidTo', {
            productTitle: product.title,
            validFrom: product.validFrom.format('L'),
            validTo: product.validFrom.format('L'),
        });
    }
    return i18next.t('product.productBookedDescription', {
        productTitle: product.title,
        validFrom: product.validFrom.format('L'),
    });
};

const updateProduct = action$ =>
    action$.ofType(Actions.update.type).mergeMap(({payload}) =>
        updateProductObservable(payload.product)
            .flatMap(action => [GlobalEventActions.updateSuccess.action(i18next.t('product.productUpdated')), action])
            .catch(e => {
                if (e.errorCode === Constants.RequiresForceUpdateErrorCode) {
                    return showDialogObservable({
                        title: i18next.t('product.productBooked'),
                        description: getProductBookedDescription(payload.product),
                    }).mergeMap(selection => {
                        if (selection === 'OK') {
                            return updateProductObservable(payload.product, true)
                                .flatMap(action => [
                                    GlobalEventActions.updateSuccess.action(
                                        i18next.t(
                                            payload.newVersion
                                                ? 'product.productChangesSaved'
                                                : 'product.productUpdated',
                                        ),
                                    ),
                                    action,
                                ])
                                .catch(() => Observable.of(Actions.updateFailed.action(payload.product)));
                        } else {
                            return Observable.of(Actions.updateAborted.action(payload.product));
                        }
                    });
                }
                return Observable.of(Actions.updateFailed.action(payload.product));
            }),
    );

const removeProduct = action$ =>
    action$.ofType(Actions.remove.type).mergeMap(({payload}) => {
        return showDialogObservable({
            title: i18next.t('product.confirmDelete'),
            description: i18next.t('product.productDeleteDescription', {productTitle: payload.product.title}),
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return removeProductObservable(payload.product)
                    .flatMap(action => [
                        GlobalEventActions.updateSuccess.action(i18next.t('product.productDeleted')),
                        action,
                    ])
                    .catch(e => {
                        if (e.errorCode === 1476097841) {
                            return showAlertObservable({
                                title: '',
                                description: i18next.t('product.productDeleteBookedError'),
                            }).mergeMap(() => Observable.of(Actions.removeFailed.action(payload.product)));
                        }

                        return Observable.of(Actions.removeFailed.action(payload.product));
                    });
            } else {
                return Observable.of(Actions.removeAborted.action(payload.product));
            }
        });
    });

const restoreProduct = action$ =>
    action$.ofType(Actions.restore.type).mergeMap(({payload}) => {
        return showDialogObservable({
            title: i18next.t('areYouSure'),
            description: i18next.t('product.productRestoreDescription'),
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return restoreProductObservable(payload.product)
                    .flatMap(action => [
                        GlobalEventActions.updateSuccess.action(i18next.t('product.productRestored')),
                        action,
                    ])
                    .catch(e => Observable.of(Actions.restoreFailed.action(e)));
            } else {
                return Observable.of(Actions.restoreAborted.action(payload.product));
            }
        });
    });

const unscheduleProduct = action$ =>
    action$.ofType(Actions.unschedule.type).mergeMap(({payload}) => {
        return showDeleteDialogObservable({
            title: i18next.t('areYouSure'),
            description: i18next.t(
                payload.last
                    ? 'plans.manageProduct.deleteDescription'
                    : 'plans.manageProduct.unscheduleChangeDescription',
                {
                    title: payload.product.title,
                    date: payload.product.validFrom.format('ll'),
                },
            ),
            okText: i18next.t('confirm'),
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return unscheduleProductObservable(payload.product.id, payload.last)
                    .flatMap(action => [
                        GlobalEventActions.updateSuccess.action(
                            i18next.t(
                                payload.last
                                    ? 'plans.manageProduct.deleteSuccessful'
                                    : 'plans.manageProduct.unscheduleSuccessful',
                                {
                                    title: payload.product.title,
                                },
                            ),
                        ),
                        action,
                    ])
                    .catch(e => Observable.of(Actions.unscheduleFailed.action(e)));
            } else {
                return Observable.of(Actions.unscheduleAborted.action(payload.product));
            }
        });
    });

export default combineEpics(
    fetchProduct,
    fetchAll,
    createProduct,
    updateProduct,
    restoreProduct,
    removeProduct,
    unscheduleProduct,
);
